import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { speakersAPI } from '../../lib/api';
import { Users, Rocket, Search, Linkedin } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { useSEO } from '../../hooks/useSEO';

export const SpeakersPage = () => {
  useSEO('Speaker');
  const [speakers, setSpeakers] = useState([]);
  const [filteredSpeakers, setFilteredSpeakers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (search) {
      const filtered = speakers.filter(s => 
        `${s.first_name} ${s.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
        s.company?.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredSpeakers(filtered);
    } else {
      setFilteredSpeakers(speakers);
    }
  }, [search, speakers]);

  const loadData = async () => {
    try {
      const res = await speakersAPI.getPublic();
      setSpeakers(res.data);
      setFilteredSpeakers(res.data);
    } catch (error) {
      console.error('Error loading speakers:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
          <Rocket className="text-primary animate-bounce" size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 px-6" data-testid="speakers-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">
            I Protagonisti
          </p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Speaker
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mb-8">
            Scopri gli speaker che hanno partecipato alle edizioni di SIOS.
            Founder, investitori, innovatori e leader del settore tech.
          </p>

          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input 
              placeholder="Cerca speaker..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 bg-white/5 border-white/10 rounded-full h-12"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Speakers Grid */}
        {filteredSpeakers.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {filteredSpeakers.map((speaker) => (
              <Link 
                key={speaker.id}
                to={`/speakers/${speaker.slug}`}
                className="group"
                data-testid={`speaker-card-${speaker.id}`}
              >
                <div className="relative aspect-[3/4] overflow-hidden rounded-xl mb-4">
                  <img 
                    src={speaker.image || `https://ui-avatars.com/api/?name=${speaker.first_name}+${speaker.last_name}&background=7B2CBF&color=fff&size=400`}
                    alt={`${speaker.first_name} ${speaker.last_name}`}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-sios-void via-transparent to-transparent" />
                  
                  {speaker.linkedin && (
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-8 h-8 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
                        <Linkedin size={14} className="text-white" />
                      </div>
                    </div>
                  )}
                </div>
                <h4 className="font-heading font-semibold group-hover:text-primary transition-colors">
                  {speaker.first_name} {speaker.last_name}
                </h4>
                {(speaker.title || speaker.company) && (
                  <p className="text-sm text-muted-foreground">
                    {speaker.title}
                    {speaker.title && speaker.company && ' @ '}
                    {speaker.company}
                  </p>
                )}
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-24">
            <Users className="mx-auto text-muted-foreground mb-4" size={48} />
            <h2 className="font-heading text-xl font-semibold mb-2">
              {search ? 'Nessun risultato' : 'Nessuno speaker disponibile'}
            </h2>
            <p className="text-muted-foreground">
              {search ? 'Prova con un altro termine di ricerca.' : 'Gli speakers verranno pubblicati presto.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
